import { GetStaticPathsResult, GetStaticPropsContext, GetStaticPropsResult } from 'next';
import invariant from 'tiny-invariant';

import { getRutilusGraphqlClient } from '../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../src/api/graphql/queryRutilusStatic';
import { AppLogic } from '../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../src/components/Layouts/GlobalLayout';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../src/constants/next';
import { reportMykissError } from '../../../src/helpers/errorHandling';
import { parseInternalId } from '../../../src/helpers/number';
import { redirectPageWithInternalId } from '../../../src/helpers/redirects/redirectPageWithInternalId';
import { GetPage, GetPageVariables } from '../../../src/interfaces/graphql';
import { IPageMetadata } from '../../../src/interfaces/nextjs';
import { getPage } from '../../../src/queries/rutilus/Page';
import { IPageProps, Page } from '../../../src/views/Page/Page';

type IParams = {
  pageId: string;
  pageSlug?: string[];
};

export async function getStaticProps({
  params,
}: GetStaticPropsContext<IParams>): Promise<GetStaticPropsResult<IPageMetadata & IPageProps>> {
  invariant(params, 'Params not found');

  try {
    const client = getRutilusGraphqlClient({ serverMode: 'static' });

    // Redirect /pages/:internalId/:slug => /pages/:externalId/:slug
    const internalId = parseInternalId(params.pageId);
    if (internalId !== null) {
      return redirectPageWithInternalId(internalId, client);
    }

    const { response } = await queryRutilusStatic<GetPage, GetPageVariables>(client, {
      query: getPage,
      variables: { id: params.pageId },
    });

    if (!response) {
      return NOT_FOUND_WITH_REVALIDATION;
    }

    return {
      props: {
        data: response.data,
        pageExternalId: params.pageId,
        _page: 'page',
        _pageTitle: 'Page',
      },
      revalidate: REVALIDATE.DAILY,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

Page.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout hideStickyBannerGetApp hideFooter>
        {page}
      </GlobalLayout>
    </AppLogic>
  );
};

export default Page;
